import type { User } from '@/types/User';
import { prefCodec, readPref, UI_PREFS, withCodec, writePref } from '@/lib/ui-prefs';

/**
 * T-68: the last person signed in on this browser, without their token, so an opened app can draw
 * for them at once while the session check runs (stores/authStore.ts starts with it). The session
 * cookie still decides: a "signed out" answer goes to /login, as before. Cleared on sign-out.
 */

const LAUNCH_USER = withCodec(UI_PREFS.launchUser, prefCodec.text);

type RememberedUser = Omit<User, 'token' | 'pendingInviteToken'>;

/** What's remembered, read from text; null when it's missing or can't be read. */
export function parseRememberedUser(raw: string | null): User | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<RememberedUser> | null;
    if (!v || typeof v.id !== 'string' || !v.id || typeof v.email !== 'string') return null;
    return {
      id: v.id,
      email: v.email,
      displayName: typeof v.displayName === 'string' ? v.displayName : '',
      photoURL: typeof v.photoURL === 'string' ? v.photoURL : '',
      handle: typeof v.handle === 'string' ? v.handle : null,
      is_admin: v.is_admin === true,
      // No token: API calls wait for the session check (lib/session-ready.ts).
      token: '',
      pendingInviteToken: null,
    };
  } catch {
    return null;
  }
}

/** The text stored for a signed-in user: everything but the token and the invite. */
export function serializeRememberedUser(user: User): string {
  const remembered: RememberedUser = {
    id: user.id,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    handle: user.handle,
    is_admin: user.is_admin,
  };
  return JSON.stringify(remembered);
}

export function rememberedUser(): User | null {
  return parseRememberedUser(readPref(LAUNCH_USER));
}

/** Remember a signed-in user (with a token), or forget (null). Writes only when it changed. */
export function rememberUser(user: User | null): void {
  const next = user ? serializeRememberedUser(user) : null;
  if (readPref(LAUNCH_USER) !== next) writePref(LAUNCH_USER, next);
}
